(ns common.schema.entities.workspace.kyc.kyc-utils
  (:require
   [common.util :as cu]
   [clojure.string :as string]
   [clojure.set :as set]
   [common.country :as country]))

(defn localize
  [meta path-or-key lang-kw]
  (-> (if (keyword? path-or-key)
        (get meta path-or-key)
        (get-in meta path-or-key))
      :translations
      (get lang-kw)))

(defn localize-option
  [questions-meta path-or-key option-key lang-kw]
  (let [question-meta (if (keyword? path-or-key)
                        (get questions-meta path-or-key)
                        (get-in questions-meta path-or-key))
        options (:options question-meta)
        option-meta (->> options
                         (filter (fn [opt]
                                   (or (= option-key (:key opt))
                                       ;; Some options do not contain the :keyword, which caused nil value to match any the first option.
                                       ;; Must first check if option contains the :keyword before trying to match
                                       (when (contains? opt :keyword)
                                         (= option-key (:keyword opt))))))
                         (first))]
    (localize option-meta [] lang-kw)))

(defn- option-identity
  "Return the option's stored value — the one that ends up in answers and
   that react-select compares against.

   Priority `:value` → `:keyword` → `:key`:
   - `:value` is the canonical wire format when present (e.g. country options).
   - `:keyword` is by convention always typed as a keyword when present, so
     it's a safer fallback than `:key`, which country options stringify
     (`:key \"FI\"` vs `:keyword :FI`).
   - `:key` is the last fallback for plain options (UBO categories etc.)
     where it's already a keyword and `:value`/`:keyword` aren't set."
  [opt]
  (or (:value opt)
      (:keyword opt)
      (:key opt)))

(defn- value-contains?
  "True if `value` either equals `k` (single-select) or contains `k`
   (multi-select set/vector). Nil-safe — and crucially refuses to match
   `nil` on either side: if `option-identity` ever fails to resolve an
   option (returns nil) we don't want every unfilled (nil-valued) field
   to accidentally match the malformed option."
  [value k]
  (cond
    (nil? k)            false
    (nil? value)        false
    (set? value)        (contains? value k)
    (sequential? value) (boolean (some #(= % k) value))
    :else               (= value k)))

(defn deprecated-option-keys
  "Set of option-keys marked `:deprecated?` in the given options seq.
   Drops any entries whose identity fails to resolve (nil) so a malformed
   option can't sneak `nil` into the set and accidentally match an unfilled
   field."
  [options]
  (->> options
       (filter :deprecated?)
       (keep option-identity)
       set))

(defn value-deprecated?
  "True if `value` (single keyword OR multi-select coll) references any
   `:deprecated?` option. Used by `copy-answers` to drop legacy values
   on process creation and by validation to treat them as missing."
  [options value]
  (let [deprecated (deprecated-option-keys options)]
    (cond
      (empty? deprecated)  false
      (nil? value)         false
      (set? value)         (boolean (some deprecated value))
      (sequential? value)  (boolean (some deprecated value))
      :else                (boolean (deprecated value)))))

(defn active-options
  "Returns the option list with deprecated options filtered out, unless an
   option is the currently-selected value — in which case it is preserved
   and marked `:disabled?` so the dropdown renders it as a greyed-out
   non-selectable item (the select wrapper normalizes `:disabled?` to
   react-select's `:isDisabled`). Once the user picks a non-deprecated
   option the deprecated one drops out entirely on the next render."
  [options current-value]
  (->> options
       (keep (fn [opt]
               (cond
                 (not (:deprecated? opt)) opt
                 (value-contains? current-value (option-identity opt))
                 (assoc opt :disabled? true)
                 :else nil)))
       (vec)))

(defn schema-questions
  [schema]
  (some->> schema
           (map second)
           (sort-by :ord)
           (mapcat (fn [meta]
                     (->> meta
                          :questions
                          (sort-by (fn [[_ q-meta]]
                                     (:ord q-meta))))))
           (map-indexed (fn [idx [q q-meta]]
                          [q (assoc q-meta :ord idx)]))))

(defn schema-export-questions
  [schema]
  (->> schema
       (schema-questions)
       (map (fn [[q m]]
              [q
               (assoc m
                      :label (->> m
                                  :translations
                                  :en)
                      :field/data-type (:data/type m))]))
       (into {})))

(defn localize-question
  [schema question lang-kw]
  (let [translations
        (->> schema
             (mapcat (fn [[section-key section-meta]]
                       (when-not (string? section-meta)
                         (concat [[section-key (select-keys section-meta [:translations])]]
                                 (->> section-meta
                                      :questions
                                      (map (fn [[q q-meta]]
                                             [q (select-keys q-meta [:translations])])))))))
             (into {}))]
    (some-> translations
            (get question)
            :translations
            (get lang-kw))))

(def ubo-nationality-keys
  {:nationalities-q      :kyc-ubo/nationalities
   :legacy-nationality-q :kyc-ubo/nationality
   :legacy-id-q          :kyc-ubo/pid})

(def board-member-nationality-keys
  {:nationalities-q      :kyc-board-member/nationalities
   :legacy-nationality-q :kyc-board-member/country})

(def person-nationality-keys
  {:nationalities-q      :kyc-person/nationalities
   :legacy-nationality-q :kyc-person/nationality
   :legacy-id-q          :kyc-person/pin})

(defn nationality-keys-for
  "Key configuration (new answer key + legacy fallback keys) for a
   nationalities question attribute."
  [nationalities-q]
  (case nationalities-q
    :kyc-ubo/nationalities ubo-nationality-keys
    :kyc-board-member/nationalities board-member-nationality-keys
    :kyc-person/nationalities person-nationality-keys
    {:nationalities-q nationalities-q}))

(defn nationality-rows
  "Rows of an :edn-data.type/nationalities answer:
   [{:ord 0 :nationality <country-kw> :id-number <string>} ...]

   When the answer key is absent from `answers`, falls back to a single row
   built from the legacy nationality + national id answers so processes saved
   before the multi-nationality field keep their data. An empty collection
   present under the key means the respondent removed every row — the legacy
   fallback does not apply then."
  [answers {:keys [nationalities-q legacy-nationality-q legacy-id-q]}]
  (if (contains? answers nationalities-q)
    (get answers nationalities-q)
    (let [nationality (get answers legacy-nationality-q)
          id-number (when legacy-id-q
                      (not-empty (str (get answers legacy-id-q))))]
      (when (or nationality id-number)
        [{:ord         0
          :nationality nationality
          :id-number   id-number}]))))

(defn nationalities-missing-data?
  "True when there are no nationality rows or any row lacks either the
   nationality or its National ID number. Legacy fallbacks are included."
  [answers nationality-keys]
  (let [rows (nationality-rows answers nationality-keys)]
    (or (empty? rows)
        (some (fn [{:keys [nationality id-number]}]
                (or (cu/blank-value? nationality)
                    (cu/blank-value? id-number)))
              rows))))

(defn nationalities-unanswered?
  "True when the nationalities question should still be treated as mandatory
   for these answers: either the respondent has touched the new answer key
   (validated on its actual value) or there is no legacy answer to fall back
   on. Used by the :mandatory-field? fns of nationalities questions so
   processes saved before the multi-nationality field are not flagged."
  [answers {:keys [nationalities-q] :as nationality-keys}]
  (or (contains? answers nationalities-q)
      (empty? (nationality-rows answers nationality-keys))))

(defn nationality-labels
  "Localized comma-separated nationality names for a nationalities answer
   (legacy fallback included)."
  [answers {:keys [nationality-keys lang-kw]}]
  (->> (nationality-rows answers nationality-keys)
       (keep (fn [row]
               (country/get-label (:nationality row) lang-kw)))
       (string/join ", ")))

(defn pep-display-name
  "Full display name for a PEP entry. Prefers the structured
   first/middle/last name answers; falls back to the legacy single
   full-name answers for entries saved before the name split."
  [pep]
  (let [structured (->> [(:kyc-ubo.pep/first-name pep)
                         (:kyc-ubo.pep/middle-names pep)
                         (:kyc-ubo.pep/last-name pep)]
                        (remove #(string/blank? (str %)))
                        (string/join " "))]
    (if (string/blank? structured)
      (or (:kyc-ubo.pep/name pep)
          (:kyc-ubo.pep/entity-representative-name pep))
      structured)))

(defn localize-header
  [header-kw lang-kw]
  (let [translations {:confirmation-status {:fi "Vahvistuksen tila" :en "Confirmation status"}
                      :actions {:fi "Toiminnot" :en "Actions"}}]
    (get-in translations [header-kw lang-kw])))

(defn localize-button-text
  [text-kw lang-kw]
  (let [translations {:confirm     {:fi "Vahvista" :en "Confirm"}
                      :confirm-all {:fi "Vahvista kaikki" :en "Confirm all"}
                      :remove      {:fi "Poista" :en "Remove"}}]))

(defn question-field-schema
  [schema question]
  (let [question-meta (some->> schema
                               (schema-questions)
                               (cu/filter-by first question)
                               (first)
                               (second))]
    (assoc question-meta
           :attribute question
           :field/data-type (:data/type question-meta)
           :label (some->> question-meta
                           :translations
                           :en))))

(defn resolve-en-option
  [question-schema value]
  (->> question-schema
       :options
       (filter (fn [o]
                 (or (= value (:key o))
                     (= value (:keyword o)))))
       (first)
       :translations
       :en))

(defn tax-countries-display-string
  [v]
  (->> v
       (sort-by :ord)
       (map (fn [c]
              (str (country/get-label (:country c))
                   "="
                   (if-not (cu/blank-value? (:tin c))
                     c
                     (:tin-missing-reason c)))))
       (string/join ";")))

(defn custom-edn-table-values-display-string
  [question-schema value]
  (->> value
       (sort-by second)
       (map (fn [[k v]]
              (str (resolve-en-option question-schema k)
                   "="
                   v)))
       (string/join ";")))

(defn display-string
  [question question-schema value]
  (case (:data/type question-schema)
    :data.type/text-simple value
    :data.type/text value
    :data.type/boolean (str value)
    :data.type/date value
    :data.type/datetime value
    :data.type/text-multiline value
    :data.type/keyword (resolve-en-option question-schema value)
    :data.type/keyword-multi (some->> value
                                      (map (fn [single-value]
                                             (resolve-en-option question-schema single-value)))
                                      (string/join ";"))
    :data.type/email value
    :data.type/currency-value value
    :data.type/text-url value
    :data.type/text-edn (case question
                          :kyc-legal-entity/split-of-investment-portfolio
                          (custom-edn-table-values-display-string question-schema value)
                          :kyc-legal-entity/source-of-funds-allocation-split
                          (custom-edn-table-values-display-string question-schema value)
                          :kyc-legal-entity/tax-countries (tax-countries-display-string value)
                          :kyc-legal-entity/board-members-edn (count value)
                          :kyc-legal-entity/ubo-edn (count value)
                          :kyc-legal-entity/pep-edn (count value)
                          :kyc-person/ubo-edn (count value)
                          :kyc-person/tax-countries (tax-countries-display-string value)
                          :kyc-ubo/tax-countries (tax-countries-display-string value)
                          nil)
    :data.type/confirm value
    :data.type/percentage value
    nil))

(defn localize-display-string
  [schema question value lang-kw]
  (let [question-schema (question-field-schema schema question)]
    (case (:data/type question-schema)
      :data.type/text-simple value
      :data.type/text value
      :data.type/boolean (str value)
      :data.type/date value
      :data.type/datetime value
      :data.type/text-multiline value
      :data.type/keyword (localize-option question-schema [] value lang-kw)
      :data.type/keyword-multi (some->>
                                value
                                (map (fn [single-value]
                                       (localize-option question-schema [] single-value lang-kw)))
                                (string/join ";"))
      :data.type/email value
      :data.type/currency-value value
      :data.type/text-url value
      :data.type/text-edn nil
      :data.type/confirm value
      :data.type/percentage value
      nil)))

(defn are-same-board-members
  "Compare board members if they are the same.
  True if first name, last name, date of birth and country/residence are the same."
  [bm1 bm2]
  (and
   (= (:kyc-board-member/first-name bm1) (:kyc-board-member/first-name bm2))
   (= (:kyc-board-member/last-name bm1) (:kyc-board-member/last-name bm2))
   (= (:kyc-board-member/dob bm1) (:kyc-board-member/dob bm2))
   (= (:kyc-board-member/country bm1) (:kyc-board-member/country bm2))))

(defn resolve-board-members-from-enrichment
  [enrichment-data-raw existing-board-members]
  (let [board-members-from-enrichment
        (->> enrichment-data-raw
             (map (fn [board-member]
                    (set/rename-keys board-member
                                     {:country    :kyc-board-member/country
                                      :role       :kyc-board-member/role
                                      :dob        :kyc-board-member/dob
                                      :first_name :kyc-board-member/first-name
                                      :last_name  :kyc-board-member/last-name}))))
        updated-board-members
        (->> existing-board-members
             (map (fn [existing-board-member]
                    (let [found-from-enrichment (some
                                                 (fn [board-member-from-enrichment]
                                                   (are-same-board-members existing-board-member
                                                                           board-member-from-enrichment))
                                                 board-members-from-enrichment)]
                      (if found-from-enrichment
                        existing-board-member
                        (assoc existing-board-member
                               :kyc-board-member/confirmation-status
                               :not-found-from-data-source))))))
        previously-not-found (->> board-members-from-enrichment
                                  (remove (fn [enrichment-bm]
                                            (some (fn [existing-bm]
                                                    (are-same-board-members enrichment-bm existing-bm))
                                                  existing-board-members)))
                                  (map (fn [board-member]
                                         (assoc board-member
                                                :kyc-board-member/confirmation-status
                                                :pending))))]
    (concat updated-board-members previously-not-found)))
